import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Button from '../components/ui/Button';
import Icon from '../components/AppIcon';
import { ROUTES } from '../constants/routes';

/**
 * AccessDenied Page
 * Displayed when a user tries to access a page they don't have permission for
 * Suggests the appropriate page based on their role
 */
const AccessDenied = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Determine the correct page based on user role
  const getSuggestedPage = () => {
    if (!user) {
      return {
        title: 'Login Page',
        description: 'Please login to access the system',
        route: ROUTES.LOGIN,
        icon: 'LogIn'
      };
    }

    switch (user.role) {
      case 'admin':
        return {
          title: 'User Management',
          description: 'Manage users, roles, and permissions',
          route: ROUTES.USER_MANAGEMENT,
          icon: 'Users'
        };
      case 'manager':
        return {
          title: 'Department Manager Hub',
          description: 'Manage your department, team, and budgets',
          route: ROUTES.DEPARTMENT_MANAGER_HUB,
          icon: 'Building'
        };
      case 'staff':
        return {
          title: 'Staff Workflow Center',
          description: 'Access your tasks, requests, and work assignments',
          route: ROUTES.STAFF_WORKFLOW_CENTER,
          icon: 'Briefcase'
        };
      case 'viewer':
        return {
          title: 'Financial Reports',
          description: 'View financial reports and analytics',
          route: ROUTES.FINANCIAL_REPORTS,
          icon: 'FileText'
        };
      default:
        return {
          title: 'Executive Dashboard',
          description: 'View system overview and analytics',
          route: ROUTES.EXECUTIVE_DASHBOARD,
          icon: 'LayoutDashboard'
        };
    }
  };

  const suggestedPage = getSuggestedPage();

  const handleGoToSuggested = () => {
    navigate(suggestedPage.route);
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleGoHome = () => {
    navigate(ROUTES.EXECUTIVE_DASHBOARD);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="max-w-2xl w-full text-center">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="rounded-full bg-destructive/10 p-6">
            <Icon 
              name="ShieldAlert" 
              size={64} 
              className="text-destructive"
            />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-4xl font-bold text-foreground mb-4">
          Access Denied
        </h1>

        {/* Description */}
        <p className="text-lg text-muted-foreground mb-8">
          You don't have permission to access this page.
          {user && (
            <span className="block mt-2">
              Your current role: <span className="font-semibold text-foreground capitalize">{user.role}</span>
            </span>
          )}
        </p>

        {/* Suggested Page Card */}
        <div className="bg-card border border-border rounded-lg p-6 mb-8 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="rounded-full bg-primary/10 p-3 flex-shrink-0">
              <Icon 
                name={suggestedPage.icon} 
                size={24} 
                className="text-primary"
              />
            </div>
            <div className="flex-1 text-left">
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Suggested: {suggestedPage.title}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {suggestedPage.description}
              </p>
              <Button
                onClick={handleGoToSuggested}
                iconName="ArrowRight"
                iconPosition="right"
                size="lg"
                className="w-full sm:w-auto"
              >
                Go to {suggestedPage.title}
              </Button>
            </div>
          </div>
        </div>

        {/* Additional Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            variant="outline"
            onClick={handleGoBack}
            iconName="ArrowLeft"
          >
            Go Back
          </Button>
          
          {user && (
            <Button
              variant="ghost"
              onClick={handleGoHome}
              iconName="Home"
            >
              Dashboard Home
            </Button>
          )}
        </div>

        {/* Help Text */}
        <div className="mt-8 pt-8 border-t border-border">
          <p className="text-sm text-muted-foreground">
            If you believe you should have access to this page, please contact your administrator.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AccessDenied;
